import { type GraphQLFilter, type GraphQLFilterExpression } from "./types"

/**
 * Maps GraphQL comparison operators to Gelly syntax
 */
const OPERATOR_MAP: Record<string, string> = {
  equals: "==",
  notEquals: "!=",
  lessThan: "<",
  lessThanOrEqual: "<=",
  greaterThan: ">",
  greaterThanOrEqual: ">=",
  before: "<",
  after: ">",
}

/**
 * Special operators that require custom handling
 */
const SPECIAL_OPERATORS = ["isSet", "in", "notIn", "startsWith", "matches"] as const

/**
 * Collection operators that wrap conditions in function calls
 */
const COLLECTION_OPERATORS = ["some", "every"] as const

/**
 * Logical operators
 */
const LOGICAL_OPERATORS = ["AND", "OR", "NOT"] as const

/**
 * Context for tracking logical operator precedence
 */
type LogicalContext = "ROOT" | "AND" | "OR"

/**
 * Transforms a GraphQL filter into Gelly syntax
 */
export const transformGraphQLFilterToGelly = (filter: GraphQLFilter, wrapInWhereClause: boolean = true): string => {
  const parser = new GraphQLToGellyParser()
  const result = parser.parse(filter)

  return wrapInWhereClause ? `[where ${result}]` : result
}

class GraphQLToGellyParser {
  parse(filter: GraphQLFilter, path: string[] = [], context: LogicalContext = "ROOT"): string {
    if (this.isLogicalOperator(filter)) {
      return this.parseLogicalOperator(filter, path, context)
    }

    return this.parseFieldFilter(filter, path, context)
  }

  private isLogicalOperator(filter: GraphQLFilter): boolean {
    if (typeof filter !== "object" || filter === null || Array.isArray(filter)) {
      return false
    }
    return LOGICAL_OPERATORS.some((op) => op in filter)
  }

  private parseLogicalOperator(filter: GraphQLFilter, path: string[], context: LogicalContext): string {
    const hasAnd = "AND" in filter
    const hasOr = "OR" in filter
    const hasNot = "NOT" in filter
    const operatorCount = [hasAnd, hasOr, hasNot].filter(Boolean).length

    // If we have multiple logical operators, treat it as a logical object
    if (operatorCount > 1) {
      return this.parseLogicalObject("AND", filter, path, context)
    }

    // Single logical operators
    if (hasNot) {
      return this.parseLogicalContent("NOT", filter.NOT, path, context)
    }

    if (hasAnd) {
      return this.parseLogicalContent("AND", filter.AND, path, context)
    }

    if (hasOr) {
      return this.parseLogicalContent("OR", filter.OR, path, context)
    }

    throw new Error("Expected logical operator but none found")
  }

  private parseLogicalContent(
    operator: "AND" | "OR" | "NOT",
    content: GraphQLFilter | GraphQLFilterExpression | GraphQLFilterExpression[],
    path: string[],
    context: LogicalContext
  ): string {
    if (operator === "NOT") {
      const innerCondition = this.parse(content as GraphQLFilter, path, "ROOT")
      // Remove outer parentheses if they exist to avoid double wrapping
      const cleanCondition = innerCondition.startsWith("(") && innerCondition.endsWith(")") ? innerCondition.slice(1, -1) : innerCondition
      return `(!(${cleanCondition}))`
    }

    if (Array.isArray(content)) {
      return this.parseLogicalArray(operator, content, path, context)
    }

    if (typeof content === "object" && content !== null) {
      return this.parseLogicalObject(operator, content, path, context)
    }

    return this.wrapInParentheses(this.parse(content as GraphQLFilter, path, operator))
  }

  private parseLogicalArray(
    operator: "AND" | "OR",
    items: (GraphQLFilterExpression | GraphQLFilter)[],
    path: string[],
    context: LogicalContext
  ): string {
    const conditions = items.map((item) => this.parse(item as GraphQLFilter, path, operator)).filter((condition) => condition.trim() !== "")

    if (conditions.length === 0) {
      return ""
    }

    const joinOperator = this.getJoinOperator(operator)
    const joined = conditions.join(joinOperator)

    return conditions.length > 1 ? this.wrapInParentheses(joined) : joined
  }

  private parseLogicalObject(
    operator: "AND" | "OR",
    content: GraphQLFilter | GraphQLFilterExpression,
    path: string[],
    context: LogicalContext
  ): string {
    const andConditions: string[] = []
    const orConditions: string[] = []

    for (const [key, value] of Object.entries(content)) {
      const handler = this.getLogicalHandler(key)
      if (handler) {
        handler(value as GraphQLFilter | GraphQLFilterExpression | GraphQLFilterExpression[], path, operator, andConditions, orConditions)
      } else {
        this.handleRegularField(
          key,
          value as GraphQLFilter | GraphQLFilterExpression | GraphQLFilterExpression[],
          path,
          operator,
          andConditions
        )
      }
    }

    return this.combineConditions(andConditions, orConditions, context)
  }

  private getLogicalHandler(key: string) {
    const handlers = new Map([
      ["AND", this.handleAndOperator.bind(this)],
      ["OR", this.handleOrOperator.bind(this)],
      ["NOT", this.handleNotOperator.bind(this)],
    ])

    return handlers.get(key)
  }

  private handleAndOperator(
    value: GraphQLFilter | GraphQLFilterExpression | GraphQLFilterExpression[],
    path: string[],
    operator: "AND" | "OR",
    andConditions: string[],
    orConditions: string[]
  ): void {
    const condition = this.parseLogicalContent("AND", value, path, operator)
    andConditions.push(condition)
  }

  private handleOrOperator(
    value: GraphQLFilter | GraphQLFilterExpression | GraphQLFilterExpression[],
    path: string[],
    operator: "AND" | "OR",
    andConditions: string[],
    orConditions: string[]
  ): void {
    const condition = this.parseLogicalContent("OR", value, path, operator)
    orConditions.push(condition)
  }

  private handleNotOperator(
    value: GraphQLFilter | GraphQLFilterExpression | GraphQLFilterExpression[],
    path: string[],
    operator: "AND" | "OR",
    andConditions: string[],
    orConditions: string[]
  ): void {
    const condition = this.parseLogicalContent("NOT", value, path, operator)
    andConditions.push(condition)
  }

  private handleRegularField(
    key: string,
    value: GraphQLFilter | GraphQLFilterExpression | GraphQLFilterExpression[],
    path: string[],
    operator: "AND" | "OR",
    andConditions: string[]
  ): void {
    const condition = this.parse(value as GraphQLFilter, [...path, key], operator)
    andConditions.push(condition)
  }

  private parseFieldFilter(filter: GraphQLFilter, path: string[], context: LogicalContext): string {
    const conditions: string[] = []

    for (const [key, value] of Object.entries(filter)) {
      const handler = this.getFieldHandler(key)
      if (handler) {
        return handler(key, value, path, context)
      }

      // Default case: process nested structures
      const nestedConditions = this.processNestedValue(value, [...path, key], context)
      conditions.push(...nestedConditions)
    }

    return this.joinConditions(conditions, " && ")
  }

  private getFieldHandler(key: string) {
    const handlers = {
      comparison: this.isComparisonOperator(key) ? this.handleComparisonOperator.bind(this) : null,
      collection: this.isCollectionOperator(key) ? this.handleCollectionOperator.bind(this) : null,
    }

    return handlers.comparison || handlers.collection
  }

  private handleComparisonOperator(
    key: string,
    value: GraphQLFilter | GraphQLFilterExpression | GraphQLFilterExpression[],
    path: string[],
    context: LogicalContext
  ): string {
    const field = path.join(".")
    return this.buildComparison(field, key, value)
  }

  private handleCollectionOperator(
    key: string,
    value: GraphQLFilter | GraphQLFilterExpression | GraphQLFilterExpression[],
    path: string[],
    context: LogicalContext
  ): string {
    let nestedCondition = this.parse(value as GraphQLFilter, path, context)
    // Remove outer parentheses if they exist (we don't want double wrapping)
    if (nestedCondition.startsWith("(") && nestedCondition.endsWith(")")) {
      nestedCondition = nestedCondition.slice(1, -1)
    }

    if (key === "some" && path.length > 0) {
      const relation = path.join(".")
      return `some(${relation}, where: ${nestedCondition})`
    }

    return `${key}(${nestedCondition})`
  }

  private processNestedValue(value: unknown, path: string[], context: LogicalContext): string[] {
    if (typeof value === "object" && !Array.isArray(value)) {
      return [this.parse(value as GraphQLFilter, path, context)]
    }

    if (Array.isArray(value)) {
      return value.map((item) => this.parse(item as GraphQLFilter, path, context))
    }

    return []
  }

  private isComparisonOperator(key: string): boolean {
    return key in OPERATOR_MAP || (SPECIAL_OPERATORS as unknown as string[]).includes(key)
  }

  private isCollectionOperator(key: string): boolean {
    return (COLLECTION_OPERATORS as unknown as string[]).includes(key)
  }

  private getJoinOperator(operator: "AND" | "OR"): string {
    return operator === "AND" ? " && " : " || "
  }

  private combineConditions(andConditions: string[], orConditions: string[], context: LogicalContext): string {
    const filteredAndConditions = andConditions.filter((condition) => condition.trim() !== "")
    const filteredOrConditions = orConditions.filter((condition) => condition.trim() !== "")

    return this.processCombination(filteredAndConditions, filteredOrConditions, context)
  }

  private processCombination(andConditions: string[], orConditions: string[], context: LogicalContext): string {
    const hasAndConditions = andConditions.length > 0
    const hasOrConditions = orConditions.length > 0

    if (!hasAndConditions && !hasOrConditions) {
      return ""
    }

    if (hasAndConditions && !hasOrConditions) {
      return this.processAndOnlyConditions(andConditions, context)
    }

    if (!hasAndConditions && hasOrConditions) {
      return this.processOrOnlyConditions(orConditions)
    }

    return this.processMixedConditions(andConditions, orConditions, context)
  }

  private processAndOnlyConditions(andConditions: string[], context: LogicalContext): string {
    const joined = andConditions.join(" && ")
    const needsParentheses = context !== "ROOT" && andConditions.length > 1
    return needsParentheses ? this.wrapInParentheses(joined) : joined
  }

  private processOrOnlyConditions(orConditions: string[]): string {
    return orConditions.length > 1 ? orConditions.join(" || ") : orConditions[0]!
  }

  private processMixedConditions(andConditions: string[], orConditions: string[], context: LogicalContext): string {
    const andPart = this.processAndOnlyConditions(andConditions, "AND")
    const orPart = this.processOrOnlyConditions(orConditions)
    const wrappedOr = this.shouldWrapOr(orPart) ? this.wrapInParentheses(orPart) : orPart
    const combined = `${andPart} && ${wrappedOr}`

    return context === "ROOT" ? this.wrapInParentheses(combined) : combined
  }

  private shouldWrapOr(orCondition: string): boolean {
    return orCondition.includes(" || ") && !orCondition.startsWith("(")
  }

  private joinConditions(conditions: string[], operator: string): string {
    const filteredConditions = conditions.filter((condition) => condition.trim() !== "")

    if (filteredConditions.length === 0) {
      return ""
    }

    if (filteredConditions.length === 1) {
      return filteredConditions[0]!
    }

    const joined = filteredConditions.join(operator)
    return this.wrapInParentheses(joined)
  }

  private wrapInParentheses(content: string): string {
    return `(${content})`
  }

  private buildComparison(
    field: string,
    operator: string,
    value: GraphQLFilter | GraphQLFilterExpression | GraphQLFilterExpression[]
  ): string {
    switch (operator) {
      case "equals":
      case "notEquals":
      case "lessThan":
      case "lessThanOrEqual":
      case "greaterThan":
      case "greaterThanOrEqual":
        return `${field} ${OPERATOR_MAP[operator]} ${JSON.stringify(value)}`
      case "isSet":
        return value ? `${field} != null` : `${field} == null`
      case "in": {
        if (!Array.isArray(value)) {
          throw new Error(`Expected array value for 'in' operator on field ${field}`)
        }
        const inConditions = value.map((v) => `${field} == ${JSON.stringify(v)}`)
        return this.wrapInParentheses(inConditions.join(" || "))
      }
      case "notIn": {
        if (!Array.isArray(value)) {
          throw new Error(`Expected array value for 'notIn' operator on field ${field}`)
        }
        const notInConditions = value.map((v) => `${field} != ${JSON.stringify(v)}`)
        return this.wrapInParentheses(notInConditions.join(" && "))
      }
      case "startsWith":
        return `startsWith(${field}, prefix: ${JSON.stringify(value)})`
      case "matches":
        return `contains(${field}, item: ${JSON.stringify(value)})`
      case "after":
        return `${field} > timestamp(${JSON.stringify(value)})`
      case "before":
        return `${field} < timestamp(${JSON.stringify(value)})`
      default:
        throw new Error(`Unknown operator: ${operator}`)
    }
  }
}
